import React, { useEffect, useState } from "react";
import { Card, Form, Button, Row, Col } from "@themesberg/react-bootstrap";
import { useHistory, useParams } from "react-router-dom";
import api from "../../utils/api";
import Swal from "sweetalert2";
// import ReactQuill from "react-quill";
// import "react-quill/dist/quill.snow.css"; // Quill theme

import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";

export default function ContentForm() {
  const history = useHistory();
  const { id } = useParams();
  const isEdit = id && id !== "new";

  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [showSource, setShowSource] = useState(false);
  const [sourceHtml, setSourceHtml] = useState("");

  useEffect(() => {
    if (isEdit) {
      api.get(`/content-pages/${id}`).then(({ data }) => {
        const n = data.data;
        setTitle(n.title || "");
        setBody(n.body || "");
      });
    }
  }, [id, isEdit]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { title, body };
      if (isEdit) await api.put(`/content-pages/${id}`, payload);
      else await api.post(`/content-pages`, payload);

      await Swal.fire({
        title: "Success",
        text: `Content page ${isEdit ? "updated" : "created"} successfully`,
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
        heightAuto: false,
      });
      history.push("/content-pages");
    } catch (e) {
      await Swal.fire({
        title: "Error",
        text: e?.response?.data?.message || e.message || "Failed to save",
        icon: "error",
        heightAuto: false,
      });
    } finally {
      setSaving(false);
    }
  };

  const editorConfiguration = {
    toolbar: [
      "undo",
      "redo",
      "|",
      "bold",
      "italic",
      "underline",
      "strikethrough",
      "|",
      "fontSize",
      "fontFamily",
      "fontColor",
      "fontBackgroundColor",
      "|",
      "highlight",
      "|",
      "alignment",
      "outdent",
      "indent",
      "|",
      "bulletedList",
      "numberedList",
      "todoList",
      "|",
      "link",
      "blockQuote",
      "insertTable",
      "horizontalLine",
      "|",
      "removeFormat",
    ],

    // 🚀 Allow ALL HTML + formatting from external sources (PDF/Word/Web)
    htmlSupport: {
      allow: [
        {
          name: /.*/, // allow every tag
          styles: true, // allow all inline styles (font-size, colors etc.)
          attributes: true, // allow all attributes (align, width, id, style)
          classes: true, // allow all CSS classes
        },
      ],
    },

    // 🏆 Best PDF/Word copy-paste formatting
    pasteFromOffice: {
      enabled: true,
      keepMarks: true,
      keepStyles: true,
    },

    // Preserve spacing, indentation, list structure
    typing: {
      transformations: {
        remove: [], // prevent CKEditor from auto-cleaning text
      },
    },

    // 💡 Don't auto clean pasted formatting
    removeFormatAttributes: false,
    removeFormatTags: [],
  };

  return (
    <Card className="shadow-sm">
      <Card.Header>
        <h5 className="mb-0">{isEdit ? "Edit" : "Create"} Content Page</h5>
      </Card.Header>
      <Card.Body>
        <Form onSubmit={onSubmit}>
          <Row>
            <Col md={8}>
              <Form.Group className="mb-3">
                <Form.Label>Title</Form.Label>
                <Form.Control
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Body</Form.Label>
                <div className="mb-2 d-flex gap-2">
                  <Button size="sm" variant={showSource ? 'secondary' : 'outline-secondary'} onClick={() => { setShowSource(s => { if(!s) setSourceHtml(body); return !s; }); }}>
                    {showSource ? 'Close HTML' : 'Edit HTML'}
                  </Button>
                  <Button size="sm" variant="outline-primary" onClick={() => { setSourceHtml('<!-- Paste HTML here -->'); setShowSource(true); }}>
                    Paste HTML
                  </Button>
                </div>
                {showSource ? (
                  <div>
                    <Form.Control as="textarea" rows={12} value={sourceHtml} onChange={(e) => setSourceHtml(e.target.value)} />
                    <div className="mt-2 d-flex gap-2">
                      <Button size="sm" variant="primary" onClick={() => { setBody(sourceHtml); setShowSource(false); }}>
                        Apply HTML
                      </Button>
                      <Button size="sm" variant="outline-secondary" onClick={() => { setShowSource(false); }}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                <CKEditor
                  editor={ClassicEditor}
                  data={body}
                  config={editorConfiguration}
                  onChange={(event, editor) => setBody(editor.getData())}
                />
                )}
              </Form.Group>
            </Col>
          </Row>
          <div className="d-flex justify-content-end">
            <Button
              variant="secondary"
              className="me-2"
              onClick={() => history.push("/content-pages")}
            >
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? "Saving..." : "Save"}
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
}
